import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject,
  output
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import * as echarts from "echarts";
import { v4 as uuidv4 } from "uuid";
import tinycolor from "tinycolor2";
import { Platform, IonIcon } from "@ionic/angular";
import { LocaleService } from "../../service/locale.service";
import { SmartPaletteCounter } from "../../class/smart-palette";
import { CounterDistributionConsumption } from "../../class/counter-distribution-consumption";
import { addHorizontalSwipeListener } from "../../class/horizontal-swipe-listener";
import { SmartComponentService } from "../../service/smart-component.service";
import { SmartComponentSummaryData } from "../../class/smart-components/smart-component-property-details";
import { LoaderService } from "src/service/loader.service";
import { ChartPieData } from "../../interfaces/chart-pie-data";
import { LoggerService } from "src/service/logger.service";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { NgxEchartsDirective } from "ngx-echarts";
import { ErrorChartComponent } from "../error-chart/error-chart.component";

@Component({
    selector: "app-chart-pie",
    templateUrl: "./chart-pie.component.html",
    styleUrls: ["./chart-pie.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonIcon, NgxEchartsDirective, ErrorChartComponent]
})
export class ChartPieComponent implements OnInit, OnDestroy {
  private readonly _translateService = inject(TranslateService);
  private readonly _platform = inject(Platform);
  private readonly _smartComponentService = inject(SmartComponentService);
  private readonly _loaderService = inject(LoaderService);
  private readonly _loggerService = inject(LoggerService);

  private _logContext: string = "Chart-pie.component";
  private readonly _localeService = inject(LocaleService);
  private _removeSwipeListener: () => void = null;
  private _titleChart: string = "";
  public uniqueId: string;
  public pieChart: any;
  public showChart: boolean = false;
  public isMobileDevice: boolean = false;
  public nextCurrentMonth: boolean = false;
  public hidePreviousMonthArrow: boolean = false;
  public errorChart: boolean = false;

  private _currentDate = new Date();
  private _chartInitialized: boolean = false;
  private _intervalId: any;

  private _dataConsumption: (CounterDistributionConsumption | ChartPieData)[] = null;

  @Input() skillVisible: boolean = true;
  @Input() enableSwipe: boolean = true;
  @Input() um: string = "";
  @Input() startDate: Date = null;
  @Input() smartPalette: SmartPaletteCounter = null;
  @Input() chartName: string = "";
  @Input() counterName: string = "";
  @Input() headerSummary: SmartComponentSummaryData = null;
  @Input() skillSummary;
  @Input() dataPieChart: ChartPieData[];
  readonly emitUpdatedDate = output<Date>();

  constructor() {
    this.isMobileDevice =
      this._platform.is("ios") || this._platform.is("android");

    // The texts of the chart are written in the code: write them again in the new language
    this._translateService.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateTitleChart());
  }

  async ngOnInit() {
    this.updateTitleChart();
    this.uniqueId = uuidv4();
    this.checkNextMonthGreaterThanCurrentMonth();
  }

  ngAfterViewInit() {
    this._intervalId = setInterval(() => {
      if (this.skillVisible && !this._chartInitialized) {
        this.initChart();
        this._chartInitialized = true;
        clearInterval(this._intervalId);
      }
    }, 100);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (this.skillVisible) {
      this.showChart = true;
    } else {
      this.showChart = false;
    }
    if (this.pieChart) {
      this.updateTitleChart();
      this.pieChart.setOption(await this.generateOption());
      this.checkNextMonthGreaterThanCurrentMonth();
      this.pieChart.resize();
    }
  }

  ngOnDestroy() {
    this._removeSwipeListener?.();
  }

  public generateShades(numberOfShades: number): string[] {
    // The pie slices are derived from the current theme color so the chart follows
    // the brand. We use the graph accent (graphBar2Color) as base
    const themeBaseColor =
      this.smartPalette?.graphBar2Color ||
      this.smartPalette?.backgroundColor ||
      "#3498db";

    const base = tinycolor(themeBaseColor);

    if (!base.isValid() || numberOfShades <= 0) {
      return [];
    }

    if (numberOfShades === 1) {
      return [base.toHexString()];
    }

    // Spread the slices evenly in lightness around the theme hue, so they stay
    // on-brand but remain visually distinguishable.
    const hsl = base.toHsl();
    const minLightness = 0.3;
    const maxLightness = 0.75;
    const shades: string[] = [];

    for (let i = 0; i < numberOfShades; i++) {
      const lightness =
        minLightness +
        ((maxLightness - minLightness) * i) / (numberOfShades - 1);

      shades.push(tinycolor({ h: hsl.h, s: hsl.s, l: lightness }).toHexString());
    }

    return shades;
  }

  /**
   * Handle click to change to previous month
   */
  public async onClickPreviousMonth() {
    await this.goToPreviousMonth();
  }

  /**
   * Handle click to change to next month
   */
  public async onClickNextMonth() {
    await this.goToNextMonth();
  }

  /**
   * For electric counter: hide left arrow if current date is january of the previous year
   */
  private checkIfIsJanuaryOfPreviousYear(): void {
    this.hidePreviousMonthArrow = false;
    const isJanuaryOfPreviousYear = ((this.startDate.getFullYear() === this._currentDate.getFullYear() - 1) && this.startDate.getMonth() === 0);

    if (this.dataPieChart && isJanuaryOfPreviousYear) {
      this.hidePreviousMonthArrow = true;
    }
  }

  /**
   * Check if next month is greater than current month
   */
  private checkNextMonthGreaterThanCurrentMonth() {
    this.nextCurrentMonth = false;
    if (
      this.startDate.getFullYear() == this._currentDate.getFullYear() &&
      this.startDate.getMonth() == this._currentDate.getMonth()
    ) {
      this.nextCurrentMonth = true;
    }
  }

  /**
   * Update title chart
   */
  private updateTitleChart() {
    this._titleChart = this._localeService.formatDate(this.startDate, "monthYear").toUpperCase();
  }

  /**
   * Get pie serie for chart for summary components
   */
  private async getPieSeriesForSummaryComponents() {
    try {
      await this._loaderService.presentLoaderWaiting();
      this._dataConsumption =
        await this._smartComponentService.getDistributionConsumption(
          this.headerSummary.idEnvironment,
          this.headerSummary.idSmartComponentKind,
          this.skillSummary,
          Number(
            this.startDate.getFullYear() +
            (this.startDate.getMonth() + 1).toString().padStart(2, "0")
          )
        );
      this._loaderService.dismissLoaderWaiting();
    } catch {
      this._loaderService.dismissLoaderWaiting();
      this.errorChart = true;
    }
  }

  /**
   * Calculate chart option
   */
  private async generateOption() {
    //API call for summary components, otherwise the data are given directly from the counter
    if (!this.dataPieChart) {
      await this.getPieSeriesForSummaryComponents();
    } else {
      this._dataConsumption = this.dataPieChart;
    }

    const shades = this.generateShades(this._dataConsumption.length);
    return {
      title: {
        text: this._titleChart,
        left: "center",
        top: this.isMobileDevice ? "top" : 10,
        textStyle: {
          fontSize: "14px",
          color: this.smartPalette?.mainLableColor || "#FFFFFF",
          fontWeight: "bold",
        },
        subtextStyle: {
          fontSize: "14px",
          color: this.smartPalette?.mainLableColor || "#FFFFFF",
          fontWeight: "bold",
        },
      },
      legend: {
        orient: "horizontal",
        left: "center",
        data: this._dataConsumption.map((item) => item.name),
        top: "80%",
        bottom: "5%",
        width: "100%",
        textStyle: {
          color: this.smartPalette?.mainLableColor || "#FFFFFF",
        },
      },
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b}: {c} ({d}%)",
      },
      series: [
        {
          name: "Categorie",
          type: "pie",
          radius: ["15%", "35%"],
          center: ["50%", "50%"],
          data: this._dataConsumption.map((item) => ({
            value: item.value,
            name: item.name,
          })),
          label: {
            show: true,
            position: "outside",
            formatter: "{b}",
            color: this.smartPalette?.mainLableColor || "#ffffff",
            textShadow: "none",
          },
          itemStyle: {
            color: (params) => {
              return shades[params.dataIndex];
            },
          },
        },
      ],
    };
  }

  /**
   * Initialize chart
   */
  private async initChart(): Promise<void> {
    const chartDom = document.getElementById(this.uniqueId) as HTMLElement;
    if (chartDom) {
      this.pieChart = echarts.init(chartDom);
      const option = await this.generateOption();
      this.pieChart.setOption(option);

      window.addEventListener("resize", () => {
        this.pieChart.resize();
      });

      if (this.enableSwipe) {
        this.addSwipeListener();
      }
    } else {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "initChart()",
        `Element with id "${this.uniqueId}" not found.`
      );
    }
  }

  /**
   * Listener for swipe event
   */
  private addSwipeListener(): void {
    const chartDom = document.getElementById(this.uniqueId);
    if (chartDom && this.isMobileDevice) {
      this._removeSwipeListener = addHorizontalSwipeListener(
        chartDom,
        async () => {
          if (!this.nextCurrentMonth) {
            await this.goToNextMonth();
          }
        },
        async () => {
          if (!this.hidePreviousMonthArrow) {
            await this.goToPreviousMonth();
          }
        }
      );
    }
  }

  /**
   * Set start and end date for the month
   * @param date
   */
  private setDates(date) {
    this.startDate = new Date(date.getFullYear(), date.getMonth(), 1);
  }

  /**
   * Go to next month
   */
  private async goToNextMonth() {
    this.setDates(
      new Date(this.startDate.getFullYear(), this.startDate.getMonth() + 1)
    );
    this.emitUpdatedDate.emit(this.startDate);
    await this.reloadPie();
  }

  /**
   * Go to previous month
   */
  private async goToPreviousMonth() {
    this.setDates(
      new Date(this.startDate.getFullYear(), this.startDate.getMonth() - 1)
    );
    this.emitUpdatedDate.emit(this.startDate);
    await this.reloadPie();
  }

  /**
   * Reload pie chart
   */
  public async reloadPie() {
    this.updateTitleChart();
    this.pieChart.setOption(await this.generateOption());
    this.checkNextMonthGreaterThanCurrentMonth();
    this.checkIfIsJanuaryOfPreviousYear();
    this.pieChart.resize();
  }
}
